import {
  Component,
  input,
  signal,
  TemplateRef,
  ViewChild,
  inject,
  Signal,
  computed,
  DestroyRef,
} from '@angular/core';
import { ChangeDetectionStrategy, OnInit } from '@angular/core';
import {
  toSignal,
  toObservable,
  takeUntilDestroyed,
} from '@angular/core/rxjs-interop';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { switchMap, of, combineLatest, startWith, BehaviorSubject } from 'rxjs';

import { VOneTabsComponent } from '@shared-components';
import { STRING_CONSTANTS } from '@shared-constants';
import { Note, NoteType, VOneTab } from '@shared-models';

import { QuotationsService } from 'src/app/@pages/quotations/quotations.service';
import { CreateNoteService } from 'src/app/@shared/services/note/create-note.service';

import { NewNoteComponent } from './components/new-note/new-note.component';
import { RelatedNotesComponent } from './components/related-notes/related-notes.component';

@Component({
  selector: 'v-one-notes',
  imports: [
    VOneTabsComponent,
    NewNoteComponent,
    RelatedNotesComponent,
    TranslateModule,
  ],
  templateUrl: './notes.component.html',
  styleUrls: ['./notes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotesComponent implements OnInit {
  // Inputs
  readonly invoiceId = input<number>();
  readonly noteType = input<NoteType>();
  readonly closeModal = input<() => void>();

  // Services
  private quotationsService = inject(QuotationsService);
  private translate = inject(TranslateService);
  private createNoteService = inject(CreateNoteService);
  private destroyRef = inject(DestroyRef);

  constructor() {
    this.createNoteService.noteCreated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.reloadNotes.next();
        this.activeTabId.set('related-notes');
      });
  }

  invoice = toSignal(
    toObservable(this.invoiceId).pipe(
      switchMap((id) =>
        id ? this.quotationsService.getQuotation(id) : of(undefined),
      ),
    ),
    { initialValue: undefined },
  );

  // The modal stays open across a note creation, and neither input changes when
  // one is registered, so the list needs a trigger of its own to reload.
  private readonly reloadNotes = new BehaviorSubject<void>(undefined);

  /**
   * Selected tab; moves to the related notes once a note is registered. The
   * tabs component writes back to it on a manual click, so a second note
   * registered without closing the modal still changes the value and reaches
   * the tab sync effect.
   */
  readonly activeTabId = signal('');

  relatedNotes: Signal<Note[]> = toSignal(
    combineLatest([
      toObservable(this.invoiceId),
      toObservable(this.noteType),
      this.reloadNotes,
    ]).pipe(
      startWith([null, null]),
      switchMap(([id, type]) => {
        if (!id || !type) return of<Note[]>([]);
        return type === 'credit'
          ? this.quotationsService.getCredito(id)
          : this.quotationsService.getDebito(id);
      }),
    ),
    { initialValue: [] as Note[] },
  );

  modalTitleShort = computed(() => {
    const type = this.noteType();

    if (!type) return '';

    return type === 'credit'
      ? this.translate.instant('global.notes.credit-note')
      : this.translate.instant('global.notes.debit-note');
  });

  totalAccredited = computed(() => {
    return this.relatedNotes().reduce((sum, note) => sum + note.total, 0);
  });

  invoiceNumber = computed(() => {
    const electronicNumber = this.invoice().billyInvoice?.invoice_number;
    const consecutiveNumber =
      this.invoice().contfac?.invoice || this.invoice().contfac?.quotation;

    if (
      electronicNumber &&
      electronicNumber.trim() !== STRING_CONSTANTS.PENDING
    ) {
      return electronicNumber;
    } else {
      return consecutiveNumber || '';
    }
  });

  modalTitle = computed(() => {
    const type = this.noteType();
    const number = this.invoiceNumber();

    if (!type || !number) return '';

    const noteTypeLabel =
      type === 'credit'
        ? this.translate.instant('global.notes.credit-note')
        : this.translate.instant('global.notes.debit-note');

    return `${noteTypeLabel} - ${number}`;
  });

  @ViewChild('newNoteTab', { static: true }) newNoteTab!: TemplateRef<unknown>;
  @ViewChild('relatedNotesTab', { static: true })
  relatedNotesTab!: TemplateRef<unknown>;

  tabs = signal<VOneTab[]>([]);

  ngOnInit() {
    this.tabs.set([
      {
        id: 'new-note',
        label: this.translate.instant('global.notes.new-note-tab'),
        content: this.newNoteTab,
      },
      {
        id: 'related-notes',
        label: this.translate.instant('global.notes.related-notes-tab'),
        content: this.relatedNotesTab,
      },
    ]);
  }

  formatDate(isoDate: string): string {
    const date = new Date(isoDate);
    return date.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }
}
